import { Locale } from '../locale/locale';

// ====================================================
// Soho Form/Element Utilities
// ====================================================

/*
 * Make elements in the jQuery selector disabled if they support the prop disabled.
 * Or has a disable method.
 * @private
 * @returns {jQuery[]} processed, jQuery-wrapped elements
 */
$.fn.disable = function () {
  $.each(this.data(), (index, value) => {
    if (value instanceof jQuery) {
      return;
    }

    if (value && value.disable) {
      value.disable();
    }
  });
  this.prop('disabled', true);
  this
    .attr('disabled', true)
    .closest('.field')
    .addClass('is-disabled');
  return this;
};

/*
 * Make elements in the jQuery selector enabled if they support the prop disabled.
 * Or has a enable method.
 * @private
 * @returns {jQuery[]} processed, jQuery-wrapped elements
 */
$.fn.enable = function () {
  $.each(this.data(), (index, value) => {
    if (value instanceof jQuery) {
      return;
    }

    if (value && value.enable) {
      value.enable();
    }
  });
  this.prop({ disabled: false, readonly: false });
  this.removeAttr('disabled readonly')
    .closest('.field')
    .removeClass('is-disabled');

  this.closest('.field').find('button.trigger').removeAttr('disabled');
  return this;
};

/*
 * Make elements in the jQuery selector readonly if they support the prop readonly.
 * Or has a readonly method.
 * @private
 * @returns {jQuery[]} processed, jQuery-wrapped elements
 */
$.fn.readonly = function () {
  $.each(this.data(), (index, value) => {
    if (value instanceof jQuery) {
      return;
    }

    if (value && value.readonly) {
      value.readonly();
    }
  });
  this.prop('readonly', true);
  return this;
};

/*
 * Make password fields or label-data fields have a reveal text to obscure or not obscure the text value.
 */
$.fn.revealText = function (settings) {
  if (!this) {
    return this;
  }

  const element = this;
  const isLabelDataField = element.find('.label, .data').length > 0;
  const isInitiallyHidden = settings?.initialState !== 'show';
  const initialIcon = isInitiallyHidden ? 'show-item' : 'hide-item';
  const localizedLabel = Locale.translate(isInitiallyHidden ? 'Show' : 'Hide');
  const createMaskedText = (text) => {
    if (!text || typeof text !== 'string') {
      return '';
    }
    const maskingType = settings?.maskingType;
    const revealCharacters = settings?.revealCharacters;

    const specialPattern = {
      email: (textToMask) => {
        const regex = /^(.{2})[^@]*(@.+)$/;
        return textToMask.replace(regex, '$1•••$2');
      },
      phone: (textToMask) => {
        const withoutCountryCode = textToMask.replace(/^\+\d{1,3}[-\s]?/, '');
        return withoutCountryCode.replace(/\d+/g, match => '•'.repeat(match.length)).replace(/-/g, ' ');
      },
      address: (textToMask) => {
        const regex = /^(\S+)\s+(.*?)\s+(\S+)$/;
        return textToMask.replace(regex, (match, first, middle, last) => {
          const maskedMiddle = middle.split(' ').map(word => '•'.repeat(word.length)).join(' ');
          return `${first} ${maskedMiddle} ${last}`;
        });
      },
      birthday: (textToMask) => {
        const regex = /^(.+ \d+), \d+$/;
        return textToMask.replace(regex, '$1');
      },
      digit: (textToMask) => {
        const regex = /,/g;
        const digitsAndDecimals = textToMask.replace(regex, '');

        if (revealCharacters) {
          const revealPosition = settings?.revealPosition || 'end';
          if (revealPosition === 'start') {
            const revealedPart = digitsAndDecimals.substring(0, revealCharacters);
            const maskedPart = '•'.repeat(digitsAndDecimals.length - revealCharacters);
            return revealedPart + maskedPart;
          }
          const revealedPart = digitsAndDecimals.substring(digitsAndDecimals.length - revealCharacters);
          const maskedPart = '•'.repeat(digitsAndDecimals.length - revealCharacters);
          return maskedPart + revealedPart;
        }

        return '•'.repeat(digitsAndDecimals.length);
      },
    };

    if (Object.keys(specialPattern).includes(maskingType)) {
      return specialPattern[maskingType](text);
    }

    if (revealCharacters) {
      const revealPosition = settings?.revealPosition || 'end';
      if (revealPosition === 'start') {
        const revealedPart = text.substring(0, revealCharacters);
        const maskedPart = text.substring(revealCharacters).replace(/[^ ]/g, '•');
        return revealedPart + maskedPart;
      }

      const revealedPart = text.substring(text.length - revealCharacters);
      const maskedPart = text.substring(0, text.length - revealCharacters).replace(/[^ ]/g, '•');
      return maskedPart + revealedPart;
    }

    return text.replace(/[^ ]/g, '•');
  };

  if (isLabelDataField) {
    // Handle label-data structure
    const dataSpan = element.find('.data');
    const originalText = dataSpan.text();

    const maskedText = createMaskedText(originalText);

    // Set initial state
    element.addClass(isInitiallyHidden ? 'field-hide-text' : 'field-show-text');

    if (isInitiallyHidden) {
      dataSpan.text(maskedText);
    }

    // Add eye icon button
    const revealText = !settings?.revealBtn || ['text', 'default'].includes(settings?.revealBtn) ?
      `<span class="text">${localizedLabel}</span>` : '';
    const revealIcon = !settings?.revealBtn || ['icon', 'default'].includes(settings?.revealBtn) ?
      `<svg class="icon reveal-icon" focusable="false" aria-hidden="true" role="presentation"><use href="#icon-${initialIcon}"></use></svg>` : '';

    let eyeButton;
    if (settings?.revealBtn === 'icon') {
      eyeButton = $(revealIcon);
    } else {
      eyeButton = $(`<button type="button" class="btn btn-reveal hide-focus" aria-label="${localizedLabel}"> 
        ${revealIcon}
        ${revealText}
      </button>`);
    }

    element.append(eyeButton);

    const self = this;
    // Handle toggle
    const toggleText = function () {
      const current = self[0];
      // skip toggling when list view is disabled
      if (current?.classList?.contains('listview-revealable-text')) {
        if (current?.parentElement?.classList?.contains('is-disabled')) return;
      }

      const isHidden = element.hasClass('field-hide-text');
      if (isHidden) {
        element.removeClass('field-hide-text').addClass('field-show-text');
        dataSpan.text(originalText);
        eyeButton.find('use').attr('href', '#icon-hide-item');
        eyeButton.find('span.text').text('Hide');
        eyeButton.attr('aria-label', Locale.translate('Hide'));
      } else {
        element.removeClass('field-show-text').addClass('field-hide-text');
        dataSpan.text(maskedText);
        eyeButton.find('use').attr('href', '#icon-show-item');
        eyeButton.attr('aria-label', Locale.translate('Show'));
        eyeButton.find('span.text').text('Show');
      }
    };

    eyeButton.on('click', toggleText);
  } else {
    // Original input field logic
    const input = element;
    input.addClass(isInitiallyHidden ? 'field-hide-text' : 'field-show-text');

    const textSpan = !settings?.revealBtn || ['text', 'default'].includes(settings?.revealBtn) ?
      `<span class="text ${settings?.revealBtn === 'text' ? 'input-hideshow-text' : ''}">${localizedLabel}</span>` : '';
    const iconSpan = !settings?.revealBtn || ['icon', 'default'].includes(settings?.revealBtn) ?
      `<svg class="icon reveal-icon ${settings?.revealBtn === 'icon' ? 'input-hideshow-text' : ''}" focusable="false" aria-hidden="true" role="presentation"><use href="#icon-${initialIcon}"></use></svg>` : '';

    let revealButton;
    if (settings?.revealBtn && settings?.revealBtn !== 'default') {
      revealButton = $(`${textSpan}${iconSpan}`);
    } else {
      revealButton = $(`<span class="input-hideshow-text text-icon-container" aria-label="${localizedLabel}"> 
        ${iconSpan}
        ${textSpan}
      </span>`);
    }

    input.after(revealButton);

    if (input.hasClass('field-hide-text')) {
      input.attr('type', 'password');
    }

    const toggleText = function () {
      const current = self[0];
      // skip toggling when list view is disabled
      if (current?.classList?.contains('listview-revealable-text')) {
        if (current?.parentElement?.classList?.contains('is-disabled')) return;
      }

      const isHidden = element.hasClass('field-hide-text');
      const textElement = revealButton.find('span.text').length === 0 && revealButton.hasClass('text') ? revealButton : revealButton.find('span.text');
      if (isHidden) {
        element.removeClass('field-hide-text').addClass('field-show-text');
        input.attr('type', 'text');

        revealButton.find('use').attr('href', '#icon-hide-item');
        textElement.text(Locale.translate('Hide'));

        revealButton.attr('aria-label', Locale.translate('Hide'));
      } else {
        element.removeClass('field-show-text').addClass('field-hide-text');
        input.attr('type', 'password');
        revealButton.find('use').attr('href', '#icon-show-item');
        revealButton.attr('aria-label', Locale.translate('Show'));
        textElement.text(Locale.translate('Show'));
      }
    };

    revealButton.on('mousedown', (e) => {
      e.preventDefault();
      toggleText(e);
    });

    input.on('keypress', (e) => {
      // Toggle on Alt key
      if (e.keyCode === 18 && e.ctrlKey) {
        toggleText();
      }
    });
  }

  return this;
};

// Fix: Labels without the "for" attribute
$(() => {
  let str;
  let control;
  const labelText = $('.label-text');
  const labels = labelText.closest('label, .label');

  labels.each(function () {
    control = $('input, textarea, select', this);
    str = control.attr('class');

    // Add "inline" and "inline-{control}" class to label
    // assuming control class is first thing in class string
    $(this).addClass(() => `inline${str ? ` inline-${str.indexOf(' ') === -1 ? str : str.substr(0, str.indexOf(' '))}` : ''}`);
  });
});

// Fix: Radio buttons was not selecting when click and than use arrow keys on Firefox
$(() => {
  $('input:radio').on('click.radios', function () {
    this.focus();
  });

  // Manage tabindex for radio button groups
  $('input:radio').on('change.radios', function () {
    const name = $(this).attr('name');
    if (name) {
      $(`input:radio[name="${name}"]`).attr('tabindex', '-1');
      $(this).attr('tabindex', '0');
    }
  });

  // Set initial tabindex on page load
  const radioGroups = {};
  $('input:radio').each(function () {
    const name = $(this).attr('name');
    if (name && !radioGroups[name]) {
      radioGroups[name] = true;
      const $checked = $(`input:radio[name="${name}"]:checked`);
      $(`input:radio[name="${name}"]`).attr('tabindex', '-1');
      if ($checked.length) {
        $checked.attr('tabindex', '0');
      } else {
        $(`input:radio[name="${name}"]`).first().attr('tabindex', '0');
      }
    }
  });
});

// Add css classes to parent for apply special rules
$(() => {
  const addCssClassToParent = function (elemArray, cssClass) {
    for (let i = 0, l = elemArray.length; i < l; i++) {
      $(elemArray[i]).parent().addClass(cssClass);
    }
  };
  addCssClassToParent($('.field > input[type="checkbox"], .field-short > input[type="checkbox"], .field > .inline-checkbox, .field > span.checkbox'), 'field-checkbox');
  addCssClassToParent($('.field > input:radio, .field > .inline-radio'), 'field-radio');
  addCssClassToParent($('.field > input:checkbox ~ label.checkbox-label.hitbox'), 'field-hitbox');
});

// Hitbox style changes for no label checkbox
$(() => {
  const checkboxes = $('.field-checkbox input:checkbox ~ label.checkbox-label.hitbox');
  checkboxes.each((idx, val) => {
    if ($(val).innerWidth() > 44) {
      $(val).addClass('hitbox-padding');
    }
  });
});

$(() => {
  // Add `aria-required` to required elements
  let elements = [].slice.call(document.body.querySelectorAll('[data-validate]'));
  elements.forEach((el) => {
    const rules = el.getAttribute('data-validate');
    if (rules && rules.indexOf('required') > -1) {
      el.setAttribute('aria-required', true);
    }
  });

  // Add `aria-hidden` to hidden elements label
  elements = [].slice.call(document.body.querySelectorAll('input[type="hidden"]'));
  elements.forEach((el) => {
    const id = el.getAttribute('id');
    if (id) {
      const label = document.body.querySelector(`label[for="${id}"]`);
      if (label) {
        label.setAttribute('aria-hidden', true);
      }
    }
  });
});

$(() => {
  const checkEllipsis = () => {
    $('label.required').each(function () {
      if (this.scrollWidth > this.clientWidth) {
        $(this).addClass('has-ellipsis');
      } else {
        $(this).removeClass('has-ellipsis');
      }
    });
  };

  // Initial check
  checkEllipsis();

  // Check on window resize
  $(window).on('resize', checkEllipsis);
});
